'use client';

/**
 * Vps.tsx — produktside for inVPS (`/vps`)
 *
 * Følger ibm.com/products-mønsteret (virtual-servers), som /webhotell og
 * /infiles: brødsmulesti og hero i grått bånd, fast seksjonsmeny, oversikt,
 * funksjoner (akkordion), promo, pakker (fire priskort), sammenligning,
 * bruksområder, relaterte tjenester, spørsmål og avsluttende CTA. Komponentene
 * ligger i src/components/product/ og deles med de andre produktsidene.
 * Ingen UI Shell (ROUTES_WITHOUT_SHELL i ClientUiShell.tsx).
 *
 * Innhold: statisk, norsk + engelsk, se vps-content.ts (plassholdere!).
 */

import ClosingCta from '@/components/landing/ClosingCta';
import LandingFooter from '@/components/landing/LandingFooter';
import LandingHeader from '@/components/landing/LandingHeader';
import { MASTHEAD_CONTENT } from '@/components/landing/masthead-content';
import VpsHeroIllustration from '@/components/invps/VpsHeroIllustration';
import VpsPanelIllustration from '@/components/invps/VpsPanelIllustration';
import ComparisonTable from '@/components/product/ComparisonTable';
import FaqAccordion from '@/components/product/FaqAccordion';
import FeatureAccordion from '@/components/product/FeatureAccordion';
import PricingCards from '@/components/product/PricingCards';
import ProductHero from '@/components/product/ProductHero';
import PromoBanner from '@/components/product/PromoBanner';
import RelatedProducts from '@/components/product/RelatedProducts';
import SectionHeader from '@/components/product/SectionHeader';
import SectionNav from '@/components/product/SectionNav';
import UseCases from '@/components/product/UseCases';
import styles from '@/components/product/product.module.css';
import { useLocale } from '@/hooks/useLocale';
import landingStyles from '@/sections/landing/landing.module.css';
import { VPS_CONTENT } from './vps-content';

export default function Vps() {
  const { locale, setLocale } = useLocale();
  const content = VPS_CONTENT[locale];

  return (
    <div className={landingStyles.page}>
      <LandingHeader ui={content.ui} menu={MASTHEAD_CONTENT[locale]} locale={locale} onLocaleChange={setLocale} />
      <main id="innhold" className={landingStyles.main}>
        <ProductHero
          hero={content.hero}
          breadcrumb={content.breadcrumb}
          illustration={<VpsHeroIllustration label={content.hero.illustrationLabel} />}
        />

        <SectionNav nav={content.sectionNav} />

        <section id="oversikt" className={styles.section} aria-labelledby="overview-heading">
          <div className={styles.container}>
            <SectionHeader
              id="overview-heading"
              eyebrow={content.overview.eyebrow}
              heading={content.overview.heading}
              blue
            >
              {content.overview.body.map((paragraph) => (
                <p key={paragraph} className={styles.lead}>
                  {paragraph}
                </p>
              ))}
            </SectionHeader>
          </div>
        </section>

        <FeatureAccordion
          features={content.features}
          art={<VpsPanelIllustration label={content.features.panelLabel} />}
        />
        <PromoBanner promo={content.promo} />
        <PricingCards plans={content.plans} />
        <ComparisonTable comparison={content.comparison} plans={content.plans} />
        <UseCases useCases={content.useCases} />
        <RelatedProducts related={content.related} />
        <FaqAccordion faq={content.faq} />
        <ClosingCta cta={content.cta} />
      </main>
      <LandingFooter
        tagline={content.footer.tagline}
        legal={content.footer.legal}
        columns={[
          {
            heading: content.footer.platformLabel,
            links: [
              { label: 'inSuite', href: '/' },
              { label: content.ui.login, href: '/login' },
            ],
          },
          {
            heading: content.footer.sectionsLabel,
            links: content.sectionNav.items.map((item) => ({ label: item.label, href: `#${item.id}` })),
          },
        ]}
      />
    </div>
  );
}
